import { create } from 'zustand';
import Cookies from 'js-cookie';
import axios from 'axios';

// Use explicit fallback for local development so API calls don't go to the page origin
const baseApiUrl = (process.env.NEXT_PUBLIC_API_URL || (process.env.NODE_ENV === 'development' ? 'http://localhost:8000' : '')).replace(/\/$/, '');
const refreshApi = axios.create({
    baseURL: baseApiUrl,
});

// Cookie options for cross-site requests
// In development we allow non-secure cookies so localhost HTTP login works.
const isProd = process.env.NODE_ENV === 'production';
const COOKIE_OPTIONS = {
    expires: 7,
    secure: isProd, // secure cookies only in production (HTTPS required)
    sameSite: isProd ? 'none' : 'lax',
    path: '/',
};

/**
 * Helper to get token from cookies or localStorage
 */
function getToken(key) {
    let value = Cookies.get(key) || '';

    // Fallback to localStorage if cookie not available
    if (!value && typeof window !== 'undefined') {
        value = localStorage.getItem(key) || '';
        if (value) {
            console.log(`Using ${key} from localStorage (cookie not available)`);
        }
    }

    return value;
}

/**
 * Helper to save token to cookies and localStorage
 */
function saveToken(key, value) {
    if (!value) return;
    Cookies.set(key, value, COOKIE_OPTIONS);
    if (typeof window !== 'undefined') {
        try {
            localStorage.setItem(key, value);
        } catch (e) {
            console.warn(`Failed to save ${key} to localStorage:`, e);
        }
    }
}

/**
 * Helper to remove token from cookies and localStorage
 */
function removeToken(key) {
    Cookies.remove(key);
    Cookies.remove(key, { path: '/' });
    Cookies.remove(key, { domain: '.ic-ces.at', path: '/' });

    if (typeof window !== 'undefined') {
        try {
            localStorage.removeItem(key);
        } catch (e) {
            // Ignore errors
        }
    }
}

/**
 * Helper to clear all auth data
 */
function clearAllAuthData() {
    const keysToRemove = [
        'access_token',
        'refresh_token',
        'username',
        'useremail',
        'userphone',
        'stayLoggedIn',
    ];

    keysToRemove.forEach(removeToken);
}

const useAuthStore = create((set, get) => ({
    isAuth: null,
    accessToken: '',
    refreshAccessTokenValue: '',
    userName: '',
    userEmail: '',
    userPhone: '',
    stayLoggedIn: false,
    refreshInProgress: false,

    /**
     * Login: save tokens and user info to state + cookies
     */
    login: (data, stayLoggedIn) => {
        // Перевірка чи існують необхідні поля
        if (!data || !data.access_token || !data.refresh_token) {
            console.error('❌ Invalid login data: missing tokens', data);
            return;
        }
        
        if (!data.user) {
            console.error('❌ Invalid login data: missing user object', data);
            return;
        }
        
        set({
            isAuth: true,
            accessToken: data.access_token,
            refreshAccessTokenValue: data.refresh_token,
            userName: data.user.name || '',
            userEmail: data.user.email || '',
            userPhone: data.user.phone || '',
            stayLoggedIn: stayLoggedIn,
        });

        // Save all tokens and user data
        saveToken('access_token', data.access_token);
        saveToken('refresh_token', data.refresh_token);
        saveToken('username', data.user.name || '');
        saveToken('useremail', data.user.email || '');
        saveToken('userphone', data.user.phone || '');

        // Save stayLoggedIn with longer expiry
        Cookies.set('stayLoggedIn', String(stayLoggedIn), { ...COOKIE_OPTIONS, expires: 365 });
        if (typeof window !== 'undefined') {
            localStorage.setItem('stayLoggedIn', String(stayLoggedIn));
        }

        console.log('Login successful, tokens saved');
    },

    /**
     * Logout: clear tokens, user info and redirect to home
     * @param {boolean} redirect - Whether to redirect to home page (default: true)
     */
    logout: (redirect = true) => {
        console.log('Logging out user...');
        clearAllAuthData();

        set({
            isAuth: false,
            accessToken: '',
            refreshAccessTokenValue: '',
            userName: '',
            userEmail: '',
            userPhone: '',
            stayLoggedIn: false,
            refreshInProgress: false,
        });

        if (redirect && typeof window !== 'undefined') {
            console.log('🔄 Redirecting to home page...');
            window.location.replace('/');
        }
    },

    /**
     * Force logout with message (for expired sessions)
     */
    forceLogout: (message = 'Your session has expired. Please log in again.') => {
        console.error('Force logout:', message);

        if (typeof window !== 'undefined') {
            sessionStorage.setItem('auth_message', message);
        }

        get().logout(true);
    },

    /**
     * Soft token refresh:
     * - returns new access token as string on success
     * - returns null on failure and triggers logout for auth errors
     */
    refreshAccessToken: async () => {
        const refreshTokenFromStorage = getToken('refresh_token');
        const stayLoggedIn = Cookies.get('stayLoggedIn') === 'true' ||
            (typeof window !== 'undefined' && localStorage.getItem('stayLoggedIn') === 'true');

        console.log('Refresh token check:', {
            hasRefreshToken: !!refreshTokenFromStorage,
            stayLoggedIn,
            refreshTokenPreview: refreshTokenFromStorage ? `${refreshTokenFromStorage.substring(0, 20)}...` : 'none',
        });

        // No refresh token - force logout
        if (!refreshTokenFromStorage) {
            console.error('❌ No refresh token found, forcing logout');
            get().forceLogout('No refresh token found. Please log in again.');
            return null;
        }

        // Prevent concurrent refresh requests
        const { refreshInProgress } = get();
        if (refreshInProgress) {
            console.warn('⏳ Refresh is already in progress, waiting...');
            await new Promise(resolve => setTimeout(resolve, 1000));
            return get().accessToken || null;
        }

        set({ refreshInProgress: true });

        try {
            const response = await refreshApi.post('/api/auth/refresh/', {
                refresh_token: refreshTokenFromStorage,
            });

            if (response.status === 200) {
                const newAccess = response.data.access_token;
                const newRefresh = response.data.refresh_token || refreshTokenFromStorage;

                console.log('Token refresh successful');

                set({
                    isAuth: true,
                    accessToken: newAccess,
                    refreshAccessTokenValue: newRefresh,
                });

                saveToken('access_token', newAccess);
                saveToken('refresh_token', newRefresh);

                const verifyAccess = getToken('access_token');
                if (!verifyAccess) {
                    console.error('Warning: Failed to save access token');
                }

                return newAccess;
            } else {
                console.error('Unexpected response status:', response.status);
                return null;
            }
        } catch (error) {
            console.error('Error refreshing token:', {
                message: error.message,
                status: error.response?.status,
                data: error.response?.data,
            });

            // CRITICAL: Handle authentication errors - force logout
            if ([400, 401, 403].includes(error.response?.status)) {
                console.error('Refresh token is invalid or expired');
                get().forceLogout('Your session has expired. Please log in again.');
                return null;
            }

            if (error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED') {
                console.warn('Network error during refresh, not logging out');
                return null;
            }

            return null;
        } finally {
            set({ refreshInProgress: false });
        }
    },

    /**
     * Check if user is authenticated and token is valid
     * Call this on protected pages
     */
    checkAuth: async () => {
        const { accessToken, refreshAccessToken } = get();

        // If no access token, try to refresh
        if (!accessToken) {
            const newToken = await refreshAccessToken();
            return !!newToken;
        }

        return true;
    },

    /**
     * Hydrate from cookies/localStorage on app start
     */
    hydrate: (data = {}) => {
        const accessToken = getToken('access_token');
        const refreshToken = getToken('refresh_token');
        const stayLoggedIn = Cookies.get('stayLoggedIn') === 'true' ||
            (typeof window !== 'undefined' && localStorage.getItem('stayLoggedIn') === 'true');

        const isAuth = !!accessToken;

        console.log('🔄 Hydrating auth state:', {
            isAuth,
            hasAccessToken: !!accessToken,
            hasRefreshToken: !!refreshToken,
            stayLoggedIn,
        });

        set({
            isAuth,
            accessToken,
            refreshAccessTokenValue: refreshToken,
            userName: getToken('username'),
            userEmail: getToken('useremail'),
            userPhone: getToken('userphone'),
            stayLoggedIn,
            ...data,
        });

        // If we have refresh token but no access token, try to refresh immediately
        if (!accessToken && refreshToken) {
            console.log('🔄 No access token but refresh token exists, attempting refresh...');
            get().refreshAccessToken().catch(error => console.error('Error refresh access token:', error));
        }
    },
}));

export default useAuthStore;